<i18n>
{
  "fr": {
    "with-specifications": "Avec spécifications",
    "linked-to-project-template": "Liés au gabarit de projet",
    "project-template": "Gabarit de projet"       
  },
  "en": {
    "with-specifications": "With specifications",
    "linked-to-project-template": "Linked to project template",
    "project-template": "Project template"  
  }
}
</i18n>

<template>
  <ui-dropdown
    v-if="projectIndicatorSelectList"
    :isSpecific="isSpecific"
    :groupedItems="projectIndicatorSelectList"
    :value="project.id"
    :dropdownWidth="200"
    expanded
    @input="onSelectProjectChange"
  ></ui-dropdown>
</template>

<script>
import { PROJECT_SORT_NAME, SORT_ORDER_ASC } from '@/consts/enums'
import UiDropdown from '@/components/ui/dropdown'
import i18n from '@/helpers/i18n'

export default {
  components: { UiDropdown },
  props: {
    indicator: { type: Object, required: true },
    project: Object,
    currentBusinessId: String
  },
  watch: {
    'indicator.id'() {
      // refresh apollo queries
      this.$apollo.queries.searchProjects.refresh()
      this.$apollo.queries.projectTemplates.refresh()
    }
  },
  computed: {
    isSpecific() {
      return this.indicator.isSpecific
    },
    projects() {
      if (!this.indicator) return []
      if (!this.indicator.prismicUid) return []
      if (!this.searchProjects) return []
      return this.searchProjects.items
    },
    templates() {
      if (!this.indicator) return []
      if (!this.indicator.prismicUid) return []
      if (!this.projectTemplates) return []
      return this.projectTemplates
    },
    projectIndicatorSelectList() {
      if (!this.indicator) return null
      if (!this.project) return null
      if (!this.projects || this.projects.length === 0) return null
      if (!this.templates || this.templates.length === 0) return null

      // Composition du tableau par catégories
      const categories = [
        {
          label: this.$t('with-specifications'),
          items: this.specificItems
        },
        {
          label: this.$t('linked-to-project-template'),
          items: this.childTemplateItems
        },
        {
          label: this.$t('project-template'),
          items: this.templateItems
        }
      ]
      return categories
    },
    templateItems() {
      const templateItems = []
      for (const template of this.templates) {
        templateItems.push({
          icon: '',
          label: template.name,
          value: {
            projectId: template.id,
            indicatorId: template.indicator.id
          }
        })
      }

      return templateItems
    },
    specificItems() {
      const specificItems = []
      for (const project of this.projects.filter((x) => x.indicator.isSpecific)) {
        specificItems.push({
          icon: '',
          label: project.name,
          value: {
            projectId: project.id,
            indicatorId: project.indicator.id
          }
        })
      }

      return specificItems
    },
    childTemplateItems() {
      const items = []
      for (const project of this.projects.filter((x) => !x.indicator.isSpecific)) {
        items.push({
          icon: '',
          label: project.name,
          value: {
            projectId: project.id,
            indicatorId: project.indicator.id
          }
        })
      }

      return items
    }
  },
  methods: {
    onSelectProjectChange(selectedProject) {
      if (!selectedProject) return null
      if (selectedProject.projectId == this.project.id && selectedProject.indicatorId == this.indicator.id) return null

      this.$router.push({
        name: this.$consts.urls.URL_INDICATOR_PROJECT,
        params: { projectId: selectedProject.projectId, id: selectedProject.indicatorId }
      })
    }
  },
  apollo: {
    searchProjects: {
      query() {
        return this.$options.query.GetRelatedProjects
      },
      variables() {
        return {
          businessId: this.currentBusinessId,
          page: 1,
          sort: PROJECT_SORT_NAME,
          order: SORT_ORDER_ASC,
          limit: 100,
          language: i18n.contentLanguage(),
          prismicUid: this.indicator.prismicUid
        }
      },
      skip() {
        return !this.indicator
      }
    },
    projectTemplates: {
      query() {
        return this.$options.query.GetRelatedProjectTemplates
      },
      variables() {
        return {
          businessId: this.currentBusinessId,
          language: i18n.contentLanguage(),
          prismicUid: this.indicator.prismicUid
        }
      },
      skip() {
        return !this.indicator
      }
    }
  }
}
</script>
<graphql>
query GetRelatedProjects(
  $businessId: ID!
  $page: Int!
  $limit: Int!
  $sort: ProjectSort!
  $order: SortOrder!
  $language: ContentLanguage!
  $prismicUid: String
) {
  searchProjects(
    businessId: $businessId
    page: $page
    limit: $limit
    sort: $sort
    order: $order
    filter: { status: { eq: ACTIVE } }
  ) {
    items {
      id
      name
      status
      indicator(language: $language, prismicUid: $prismicUid) {
        id
        language
        isSpecific
      }
    }
  }
}

query GetRelatedProjectTemplates($businessId: ID!, $language: ContentLanguage!, $prismicUid: String) {
  projectTemplates(businessId: $businessId) {
    id
    name
    status
    indicator(language: $language, prismicUid: $prismicUid) {
      id
      language
      isSpecific
    }
  }
}
</graphql>